export interface LocationLike {
  host: string
  hostname: string
  origin: string
  protocol: string
}

const FALLBACK_API_BASE_URL = 'http://localhost:8000'

const normalizePath = (value: string): string => {
  if (!value.startsWith('/')) {
    return `/${value}`
  }

  return value
}

const getLocation = (location?: LocationLike): LocationLike | undefined => {
  if (location) {
    return location
  }

  if (typeof window !== 'undefined') {
    return window.location
  }

  return undefined
}

/**
 * Infer the backend API base URL for the current runtime environment.
 *
 * @param options - Optional location and environment overrides for testing.
 * @returns string The API base URL to use for HTTP requests.
 */
export const inferApiBaseUrl = (options?: {
  isDev?: boolean
  location?: LocationLike
}): string => {
  const location = getLocation(options?.location)
  const isDev = options?.isDev ?? import.meta.env.DEV

  if (location) {
    if (isDev) {
      return `${location.origin}/backend`
    }

    return location.origin
  }

  return FALLBACK_API_BASE_URL
}

/**
 * Resolve the effective API base URL, preferring an explicit environment override when set.
 *
 * @param explicitBaseUrl - Optional explicit API base URL.
 * @param options - Optional location and environment overrides for testing.
 * @returns string The resolved API base URL.
 */
export const resolveApiBaseUrl = (
  explicitBaseUrl?: string,
  options?: {
    isDev?: boolean
    location?: LocationLike
  }
): string => {
  if (explicitBaseUrl) {
    return explicitBaseUrl
  }

  return inferApiBaseUrl(options)
}

/**
 * Build a WebSocket URL from the configured HTTP base URL and a path.
 *
 * @param path - The target WebSocket route path.
 * @param options - Optional explicit override or test-specific base data.
 * @returns string The resolved WebSocket URL.
 */
export const resolveWebSocketUrl = (
  path: string,
  options?: {
    baseUrl?: string
    location?: LocationLike
    overrideUrl?: string
  }
): string => {
  if (options?.overrideUrl) {
    return options.overrideUrl
  }

  const normalizedPath = normalizePath(path)
  const baseUrl = options?.baseUrl

  if (baseUrl) {
    try {
      const apiUrl = new URL(baseUrl)
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:'
      const normalizedBasePath = apiUrl.pathname.replace(/\/$/, '')
      const basePath = normalizedBasePath === '/' ? '' : normalizedBasePath
      return `${wsProtocol}//${apiUrl.host}${basePath}${normalizedPath}`
    } catch (error) {
      console.warn('Falling back to window location for WebSocket URL due to invalid API base URL', error)
    }
  }

  const location = getLocation(options?.location)
  const protocol = location?.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = location?.host ?? 'localhost:8000'
  return `${protocol}//${host}${normalizedPath}`
}
